import {
  Box,
  CircularProgress,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { consumerAPI } from 'api/consumerAPI'
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import SwipableStatusCard from './SwipableStatusCard'

const Status = () => {
  const drawerSettings = (progress: string) => {
    const lookup = [
      {
        progress: 'OnRoute',
        interval: 15000,
        drawerBleed: 120,
        drawerHeight: 30,
      },
      { progress: 'Driver_Allocated', interval: 15000, drawerBleed: 120 },
      {
        progress: 'Arrived',
        interval: 5000,
        drawerBleed: 120,
        drawerHeight: 30,
      },
      {
        progress: 'Passenger_Picked_Up',
        interval: 30000,
        drawerBleed: 90,
        drawerHeight: 30,
      },
      { progress: 'Completed', interval: 60000, drawerHeight: 45 },
      { progress: 'Completed_Pending_Invoice', interval: 60000 },
    ]

    return {
      interval: lookup.find((val) => val.progress === progress)?.interval,
      bleed: lookup.find((val) => val.progress === progress)?.drawerBleed,
      drawerHeight: lookup.find((val) => val.progress === progress)
        ?.drawerHeight,
    }
  }
  const theme = useTheme<Theme>()
  const [drawerBleeding, setDrawerBleeding] = React.useState(80)
  const [interval, setInterval] = React.useState(10000)
  const { path: communicationReference } = useParams<{ path: string }>()
  const [open, setOpen] = React.useState(false)
  const [eta, setEta] = React.useState<number>()
  const [drawerHeight, setDrawerHeight] = React.useState<number>(50)
  const commRef = communicationReference
    ? decodeURIComponent(communicationReference)
    : undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['getBookingTracking', commRef],
    enabled: !!commRef,
    refetchInterval: interval,
    refetchOnWindowFocus: false,
    queryFn: async () => await consumerAPI.getBookingTracking(commRef!),
  })

  useEffect(() => {
    if (data) {
      setInterval(drawerSettings(data.data.tracking.progress).interval || 30000)
      setDrawerBleeding(drawerSettings(data.data.tracking.progress).bleed || 80)
      setOpen(['Arrived'].includes(data.data.tracking.progress) ? true : open)
      setDrawerHeight(
        drawerSettings(data.data.tracking.progress).drawerHeight || 50,
      )
    }
  }, [data])

  if (isLoading) {
    return (
      <Box
        component="div"
        sx={{
          width: '100%',
          minHeight: 'var(--app-vh)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: 5,
          backgroundColor: '#ffffff',
        }}
      >
        <Typography>Getting tracking info...</Typography>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        component="div"
        sx={{
          width: '100%',
          minHeight: 'var(--app-vh)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          backgroundColor: '#ffffff',
        }}
      >
        <Typography color="warning">
          Unable to get tracking information.
        </Typography>
      </Box>
    )
  }

  const Title = () => (
    <Box
      sx={{
        color: '#fff',
        padding: '10px 16px',
        fontSize: '18px',
        letterSpacing: '1.80px',
        backgroundColor: theme.palette.brand.main,
        width: '100%',
      }}
    >
      TRIP DETAILS
    </Box>
  )

  return (
    <>
      <Box
        sx={{
          width: '100%',
          minHeight: 'var(--app-vh)',
          display: 'flex',
          overflow: 'unset',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Title />

        <SwipableStatusCard
          drawerBleeding={drawerBleeding}
          type={data?.data.tracking.status}
          progress={data?.data.tracking.progress}
          tracking={data?.data.tracking}
          data={data?.data}
          open={open}
          setOpen={setOpen}
          drawerHeight={drawerHeight}
          eta={eta}
          setEta={setEta}
        />
      </Box>
    </>
  )
}

export default Status
